import config from "../config";
import LogRocket from "logrocket";
import * as Sentry from "@sentry/react";
import axios from "axios";

export const truncateString = (stringToTruncate, length) => {
  return stringToTruncate?.length > length ? stringToTruncate.slice(0, length) + "..." : stringToTruncate;
}

export const updateIntakeFormsImmutable = (list, payload) => {
  const data = list.find((d) => d._id.toString() === payload._id.toString());
  if (data) {
    const index = list.findIndex(
      (d) => d._id.toString() === payload._id.toString()
    );

    return [
      ...list.slice(0, index),
      { ...data, ...payload },
      ...list.slice(index + 1),
    ];
  }
  return list;
};

export const updateImmutable = (list, payload, listIdName="_id", payloadIdName="_id", moveUpdatedToStart=false) => {
  const data = list.find((d) => d[listIdName] === payload[payloadIdName]);
  if (data) {
    const index = list.findIndex((d) => d[listIdName] === payload[payloadIdName]);

    if (!moveUpdatedToStart) {
      return [
        ...list.slice(0, index),
        { ...data, ...payload },
        ...list.slice(index + 1),
      ];
    } else if (moveUpdatedToStart) {
      return [
        { ...data, ...payload },
        ...list.slice(0, index),
        ...list.slice(index + 1),
      ];
    }
  }
  return list;
};

export const createMarkup = (text) => {
  return { __html: text };
};

export const capitalizeFirstLetter = (text) =>
  text &&
  text
    .split(" ")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

export const amountFormatter = (num) =>
  Number(num || 0)
    .toLocaleString("en", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
    .replace(".00", "");
export const amountFormatterWithZero = (num) =>
  Number(num || 0).toLocaleString("en", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export const isExternalRegexClosure = (url) => {
  let domainRe = /https?:\/\/((?:[\w\d-]+\.)+[\w\d]{2,})/i;
  if (domainRe.exec(url)) {
    return url;
  } else {
    url = `http://${url}`;
    return url;
  }
};

export const isUrlValid = (userInput) => {
  const res = userInput.match(
    // eslint-disable-next-line no-useless-escape
    /(http(s)?:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)/g
  );
  if (res == null) return false;
  else return true;
};

export const getFormattedTime = () => {
  const today = new Date();
  const y = today.getFullYear();
  const m = today.getMonth() + 1;
  const d = today.getDate();
  const h = today.getHours();
  const mi = today.getMinutes();
  const s = today.getSeconds();
  return d + "-" + m + "-" + y + "-" + h + "" + mi + "" + s;
};

export const capitalize = (lower) => {
  return lower.toLowerCase().replace(/(?:^|\s)\S/g, function (a) {
    return a.toUpperCase();
  });
};

export const subDomain = () => {
  return config.APP_TYPE === "local"
    ? ""
    : window.location.host.replace("www.", "").split(".")[0];
};

export const eventUrl = (eventUrl) => {
  return config.APP_TYPE === "local" ? `/${eventUrl}/` : "/";
};

export const allowAlphaNumericSpace = (e) => {
  const code = "charCode" in e ? e.charCode : e.keyCode;
  if (
    code === 32 && // space
    !(code > 47 && code < 58) && // numeric (0-9)
    !(code > 96 && code < 123)
  ) {
    // lower alpha (a-z)
    e.preventDefault();
  }
};

export const keyDownHandlerNumber = (event) => {
  if (
    event.ctrlKey ||
    event.altKey ||
    (47 < event.keyCode && event.keyCode < 58 && event.shiftKey === false) ||
    (95 < event.keyCode && event.keyCode < 106) ||
    event.keyCode === 8 ||
    event.keyCode === 9 ||
    (event.keyCode > 34 && event.keyCode < 40) ||
    event.keyCode === 46
  ) {
    return null;
  } else {
    event.preventDefault();
  }
};

export const isNumberKey = (evt) => {
  const charCode = evt.which ? evt.which : evt.keyCode;
  if (charCode !== 46 && charCode > 31 && (charCode < 48 || charCode > 57)) {
    evt.preventDefault();
    return false;
  }
  return true;
};

export const onCheckEmpty = (data) => {
  if (typeof data === "object") {
    if (JSON.stringify(data) === "{}" || JSON.stringify(data) === "[]") {
      return true;
    } else if (!data) {
      return true;
    }
    return false;
  } else if (typeof data === "string") {
    if (data.trim()) {
      return true;
    }
    return false;
  } else if (typeof data === "undefined") {
    return true;
  } else {
    return false;
  }
};

// Module-level session error accumulator — persists for the lifetime of the page.
// Do NOT reset between saveLogRocketSession calls; errors accumulate across retries.
let _sessionErrors = [];
let _sentryEventIds = [];

/**
 * Record an error that occurred during this session.
 * Call this whenever any error occurs (payment failure, API error, etc.)
 * @param {Object} opts - { errorType, context, message, sentryEventId }
 */
export const trackSessionError = ({ errorType, context, message, sentryEventId } = {}) => {
  _sessionErrors.push({
    errorType: errorType || "unknown",
    context: context || null,
    message: message || null,
    timestamp: new Date().toISOString(),
    sentryEventId: sentryEventId || null,
  });
  if (sentryEventId) _sentryEventIds.push(sentryEventId);
};

/**
 * Capture an error in Sentry with LogRocket session URL attached,
 * then record the Sentry event ID in the session accumulator.
 * Use this instead of calling Sentry.captureException directly.
 * @param {Error|any} error
 * @param {Object} opts - { errorType, context }
 */
export const captureError = (error, { errorType, context } = {}) => {
  try {
    Sentry.setContext("logrocket", {
      sessionURL: LogRocket.sessionURL,
    });
    Sentry.captureException(error, {
      tags: { errorType },
      extra: { context },
    });
    const sentryEventId = Sentry.lastEventId();
    trackSessionError({
      errorType,
      context,
      message: error?.message || String(error),
      sentryEventId,
    });
    return sentryEventId;
  } catch (e) {
    // Sentry not available
    return null;
  }
};

export const addBreadcrumb = (breadcrumb) => {
  try {
    Sentry.addBreadcrumb(breadcrumb);
  } catch (e) {
    // Sentry not available
  }
};

/**
 * Identify a user in LogRocket for session replay.
 * @param {string} userId
 * @param {Object} traits - { email, phone, name, eventUrl, ...extra }
 */
export const identifyLogRocketUser = (userId, traits = {}) => {
  try {
    const hostParts = window.location.hostname.split(".");
    const domain = hostParts.slice(-2).join(".");
    const identifyData = {
      email: traits.email || "",
      phone: traits.phone || "",
      name: traits.name || "",
      eventUrl: traits.eventUrl || subDomain() || "",
      domain,
      ...traits,
    };
    if (identifyData.email || identifyData.phone) {
      LogRocket.identify(userId, identifyData);
    }
  } catch (e) {
    // LogRocket not available
  }
};

/**
 * Set Sentry user context for error tracking.
 * @param {Object} user - User object
 * @param {Object} setting - Setting object (optional, for organizationId)
 */
export const identifySentryUser = (user, setting = null) => {
  try {
    if (!user?.id && !user?._id) return;
    Sentry.setUser({
      id: user.id || user._id,
      email: user.email || "",
      phone: user.phone || "",
      username: `${user.first || ""} ${user.last || ""}`.trim() || user.name || "",
      eventUrl: user.eventUrl || subDomain() || "",
      organizationId: setting?.organizationId || "",
      url: window.location.href,
    });
  } catch (e) {
    // Sentry not available
  }
};

/**
 * Fire-and-forget: save the current LogRocket session URL to LoggerRecordings.
 * Uses raw axios (not authenticated API wrapper) so it works for unauthenticated guests too.
 * @param {Object} opts - { userId, userTransactionsId, eventUrl, organizationId, eventType,
 *   outcome, gateway, payMethod, amount, itemType, metadata }
 */
export const saveLogRocketSession = (opts = {}) => {
  try {
    const postSession = (sessionUrl) => {
      if (!sessionUrl) return;
      const hasErrors = opts.eventType === "error" || opts.outcome === "failure" || _sessionErrors.length > 0;
      const outcome = opts.outcome || (opts.eventType === "error" || opts.outcome === "failure" ? "failure" : "success");
      axios.post(`${config.API_URL}logger-recordings`, {
        sessionUrl,
        userId: opts.userId || null,
        userTransactionsId: opts.userTransactionsId || null,
        eventUrl: opts.eventUrl || subDomain() || null,
        sosCustomersId: opts.sosCustomersId || null,
        eventType: opts.eventType || "transaction",
        outcome,
        hasErrors,
        errors: _sessionErrors.length > 0 ? [..._sessionErrors] : null,
        metadata: {
          ...(opts.metadata || {}),
          gateway: opts.gateway || null,
          payMethod: opts.payMethod || null,
          amount: opts.amount || null,
          itemType: opts.itemType || null,
          page: window.location.pathname,
          sentryEventIds: [..._sentryEventIds],
        },
      }).catch(() => {
        // fire-and-forget
      });
    };

    if (LogRocket.sessionURL) {
      postSession(LogRocket.sessionURL);
    } else if (typeof LogRocket.getSessionURL === "function") {
      LogRocket.getSessionURL(postSession);
    }
  } catch (e) {
    // LogRocket or axios not available
  }
};

/**
 * Clear Sentry user context (call on logout).
 */
export const clearSentryUser = () => {
  try {
    Sentry.setUser(null);
  } catch (e) {
    // Sentry not available
  }
};

/**
 * Read the stored auth token from localStorage.
 * Handles both JSON-stringified (new format) and raw JWT (legacy format).
 */
export const getStoredToken = () => {
  const raw = localStorage.getItem('token');
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch (e) {
    return raw;
  }
};

export function detectInAppBrowser() {
    const ua = navigator.userAgent || '';
    if (/\bGSA\//.test(ua)) return { inApp: true, app: 'Google' };
    if (/\bGMail\b/i.test(ua) || /\bGoogleMail\b/i.test(ua)) return { inApp: true, app: 'Gmail' };
    if (/\bFBAN\b|\bFBIOS\b|\bFB_IAB\b|\[FB/.test(ua)) return { inApp: true, app: 'Facebook' };
    if (/\bInstagram\b/i.test(ua)) return { inApp: true, app: 'Instagram' };
    if (/\bTwitter\b/i.test(ua)) return { inApp: true, app: 'Twitter' };
    if (/\bLinkedIn\b/i.test(ua)) return { inApp: true, app: 'LinkedIn' };
    if (/\bSnapchat\b/i.test(ua)) return { inApp: true, app: 'Snapchat' };
    if (/\bTikTok\b/i.test(ua) || /\bMusical_ly\b/i.test(ua) || /\bBytedance\b/i.test(ua)) return { inApp: true, app: 'TikTok' };
    if (/\bPinterest\b/i.test(ua)) return { inApp: true, app: 'Pinterest' };
    if (/\bLine\//i.test(ua)) return { inApp: true, app: 'LINE' };
    if (/\bMicroMessenger\b/i.test(ua)) return { inApp: true, app: 'WeChat' };
    if (/; wv\)/.test(ua)) return { inApp: true, app: 'WebView' };
    if (/iPhone|iPad/.test(ua) && /\bMobile\b/.test(ua) && !/\bSafari\b/.test(ua)) return { inApp: true, app: 'WebView' };
    return { inApp: false, app: null };
}

export function getInAppBrowserInstructions(app) {
    const isIOS = /iPhone|iPad/i.test(navigator.userAgent);
    const isAndroid = /Android/i.test(navigator.userAgent);
    switch (app) {
        case 'Instagram':
            return 'Tap the three dots (\u2026) in the top right, then "Open in Browser"';
        case 'Facebook':
            return isIOS
                ? 'Tap the Safari icon at the bottom of the screen'
                : 'Tap the three dots menu at the top right, then "Open in Chrome"';
        case 'TikTok':
            return 'Tap the three dots (\u2026), then "Open in Browser"';
        default:
            return isIOS
                ? 'Tap the (\u2026) menu at the bottom, then "Open in Safari"'
                : isAndroid
                    ? 'Tap the three dots menu at the top right, then "Open in Chrome"'
                    : 'Open this link in your default browser';
    }
}

export async function copyCurrentUrl() {
    const url = window.location.href;
    try {
        await navigator.clipboard.writeText(url);
        return true;
    } catch (e) {
        // Fallback for older browsers / restricted contexts
        const textarea = document.createElement('textarea');
        textarea.value = url;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        try {
            document.execCommand('copy');
            document.body.removeChild(textarea);
            return true;
        } catch (e2) {
            document.body.removeChild(textarea);
            return false;
        }
    }
}
